import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import RegisterAccountModal from '@/components/RegisterAccountModa';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePageSeo } from '@/hooks/usePageSeo';
import { useAuth0 } from '@auth0/auth0-react';
import { CheckIcon, CloudUpload, Handshake } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const Agents = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const [isAgentTypeDialogOpen, setIsAgentTypeDialogOpen] = useState(false);

  usePageSeo({
    title: 'For Real Estate Agents — Free Property Tax Appeal Tool | PropertyTaxHero',
    description:
      'PropertyTaxHero gives real estate agents a free tool to help clients appeal property tax assessments. Upload your client list, automate follow-ups, and build lasting relationships.',
    path: '/agents',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'PropertyTaxHero for Real Estate Agents',
      url: 'https://www.propertytaxhero.com/agents',
      description:
        'Free property tax appeal tool for real estate agents to help clients challenge overassessments.',
    },
  });

  const handleCreateAccountClick = () => {
    window.localStorage.setItem('user_type', 'AGENT');

    loginWithRedirect({
      appState: {
        returnTo: '/dashboard?tab=profile',
      },
      authorizationParams: {
        screen_hint: 'signup',
      },
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <Dialog open={isLoginDialogOpen} onOpenChange={setIsLoginDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Coming Soon</DialogTitle>
            <DialogDescription>
              In the future, this will take you to the login screen, but for now
              click below to go to the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button asChild onClick={() => setIsLoginDialogOpen(false)}>
              <Link to="/dashboard?tab=profile">Go to Dashboard</Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <RegisterAccountModal
        isOpen={isAgentTypeDialogOpen}
        setIsOpen={() => setIsAgentTypeDialogOpen(false)}
      />

      <section className="bg-tax-softWhite py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-tax-deepBlue">
              Free Property Tax Appeal Tool for Real Estate Agents
            </h1>
            <p className="text-xl md:text-2xl text-tax-neutralGray max-w-3xl mx-auto mb-8">
              A free tool for real estate agents to stay in touch, deliver
              value, and deepen relationships—with no extra work. Help your
              clients insure they aren't overpaying on their property taxes.
            </p>
            {!isAuthenticated && (
              <Button
                onClick={handleCreateAccountClick}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6 px-8"
              >
                Create Your Account
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-tax-deepBlue">
            Why Agents Love It
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Benefit 1 */}
            <div className="flex flex-col items-center text-center p-6">
              <div className="w-12 h-12 mb-4 text-tax-actionOrange">
                <CheckIcon className="w-full h-full" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-tax-deepBlue">
                100% Free to Use
              </h3>
              <p className="text-tax-neutralGray">
                No subscriptions, no upsells, ever.
              </p>
            </div>

            {/* Benefit 2 */}
            <div className="flex flex-col items-center text-center p-6">
              <div className="w-12 h-12 mb-4 text-tax-actionOrange">
                <CloudUpload className="w-full h-full" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-tax-deepBlue">
                Invite Your Clients Easily
              </h3>
              <p className="text-tax-neutralGray">
                Add clients one-by-one or upload a list—we'll handle the rest
                and invite them on your behalf.
              </p>
            </div>

            {/* Benefit 3 */}
            <div className="flex flex-col items-center text-center p-6">
              <div className="w-12 h-12 mb-4 text-tax-actionOrange">
                <CheckIcon className="w-full h-full" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-tax-deepBlue">
                Automated Annual Follow-Up
              </h3>
              <p className="text-tax-neutralGray">
                We reconnect you with your clients each year when property tax
                assessments are released.
              </p>
            </div>

            {/* Benefit 4 */}
            <div className="flex flex-col items-center text-center p-6">
              <div className="w-12 h-12 mb-4 text-tax-actionOrange">
                <Handshake className="w-full h-full" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-tax-deepBlue">
                You're the Expert
              </h3>
              <p className="text-tax-neutralGray">
                If your client needs deeper help, we refer them back to
                you—positioning you as your go-to advisor.
              </p>
            </div>

            {/* Benefit 5 */}
            <div className="flex flex-col items-center text-center p-6">
              <div className="w-12 h-12 mb-4 text-tax-actionOrange">
                <CheckIcon className="w-full h-full" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-tax-deepBlue">
                No Manual Comp Pulling
              </h3>
              <p className="text-tax-neutralGray">
                Stop manually pulling comps for every client. Let us automate
                the process and elevate your value.
              </p>
            </div>

            {/* Benefit 6 */}
            <div className="flex flex-col items-center text-center p-6">
              <div className="w-12 h-12 mb-4 text-tax-actionOrange">
                <CheckIcon className="w-full h-full" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-tax-deepBlue">
                Private & Secure
              </h3>
              <p className="text-tax-neutralGray">
                Your data is safe. We're powered by MortgageNews.org—a free,
                consumer-first platform. We never sell your information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Section */}
      <section className="py-16 bg-tax-lightGray">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h2 className="text-3xl font-bold mb-6 text-tax-deepBlue">
                Easily upload your client list and let us do the rest.
              </h2>
              <p className="text-lg text-tax-neutralGray mb-6">
                Our intuitive dashboard makes it simple to manage your clients
                and track their property tax appeals progress.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckIcon className="w-6 h-6 text-tax-actionOrange mr-2" />
                  <span className="text-tax-neutralGray">
                    Bulk upload your client database
                  </span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="w-6 h-6 text-tax-actionOrange mr-2" />
                  <span className="text-tax-neutralGray">
                    Automated personalized emails to your clients
                  </span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="w-6 h-6 text-tax-actionOrange mr-2" />
                  <span className="text-tax-neutralGray">
                    Track engagement and follow-ups
                  </span>
                </li>
              </ul>
            </div>
            <div className="md:w-1/2 bg-white p-4 rounded-lg shadow-md">
              <img
                src="/lovable-uploads/02fef0ff-2c7a-4353-94da-26562103aee8.png"
                alt="Agent Dashboard Preview showing bulk import clients interface"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto max-w-4xl px-4">
          <blockquote className="text-center">
            <p className="text-2xl font-light italic mb-6 text-tax-deepBlue">
              "Love it! It keeps me top of mind without the time sink. It took
              me five minutes to invite all my clients and now it's on auto
              pilot."
            </p>
            <footer className="text-lg text-tax-neutralGray">
              — Sarah K., Denver Realtor
            </footer>
          </blockquote>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-tax-deepBlue text-white">
        <div className="container mx-auto max-w-5xl px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Start in under 60 seconds—no credit card, no commitment.
          </h2>
          {!isAuthenticated && (
            <div className="flex flex-col sm:flex-row justify-center gap-4 items-center">
              <Button
                onClick={handleCreateAccountClick}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6 px-8 w-full sm:w-auto"
              >
                Create a Free Account
              </Button>
              <div className="text-white">
                <Button
                  variant="link"
                  className="text-white underline hover:text-tax-lightGray"
                  onClick={() => setIsLoginDialogOpen(true)}
                >
                  Already have an account? Log In
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Privacy Disclosure */}
      <section className="py-8 bg-tax-lightGray border-t border-tax-borderGray">
        <div className="container mx-auto max-w-5xl px-4">
          <details className="text-sm text-tax-neutralGray">
            <summary className="cursor-pointer font-medium mb-2">
              Privacy & Legal Information
            </summary>
            <div className="pl-4 mt-2">
              <p className="mb-2">
                PropertyTaxHero.Com is powered by MortgageNews.org—a free,
                consumer-first platform dedicated to helping homeowners save
                money.
              </p>
              <p>
                We never sell your personal information. All data is securely
                stored and encrypted. For more information, please see our{' '}
                <Link
                  to="/privacy"
                  className="text-tax-brightBlue hover:underline"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </details>
        </div>
      </section>

      {/* Sticky CTA for mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-white shadow-md border-t border-tax-borderGray p-4 sm:hidden">
        <Button
          onClick={handleCreateAccountClick}
          className="bg-primary hover:bg-primary/90 text-primary-foreground w-full"
        >
          Create Your Free Account
        </Button>
      </div>

      <Footer />
    </div>
  );
};
export default Agents;
